import { Link } from "@tanstack/react-router";
import { Flame, ArrowUpRight, Globe } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { boardsOverviewQuery, categoriesQuery, trackListingClick } from "@/lib/queries";
import { outboundUrl } from "@/lib/behot";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

const MEDAL = ["text-primary", "text-foreground/70", "text-muted-foreground"];

/** Compact podium (1-2-3) for every category board, shown under the General board. */
export function AllBoardsPodium() {
  const { t } = useT();
  const { data: categories = [] } = useQuery(categoriesQuery);
  const { data: overview } = useQuery(boardsOverviewQuery);

  if (!overview) return null;

  const boards = categories
    .filter((c) => (overview.get(c.id)?.top.length ?? 0) > 0)
    .sort((a, b) => (overview.get(b.id)!.heat ?? 0) - (overview.get(a.id)!.heat ?? 0));

  if (boards.length === 0) return null;

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2">
        <Flame className="size-4 text-primary" />
        <h3 className="font-display text-lg font-bold tracking-tight">{t("home.podium.title")}</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{t("home.podium.sub")}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {boards.map((cat) => {
          const top = overview.get(cat.id)!.top;
          return (
            <div key={cat.id} className="card-surface p-4">
              <Link
                to="/c/$slug"
                params={{ slug: cat.slug }}
                className="text-[11px] font-bold uppercase tracking-widest text-primary hover:underline"
              >
                {cat.name}
              </Link>
              <ol className="mt-2 space-y-2">
                {top.map((entry, i) => (
                  <li key={entry.listing.id} className="flex items-center gap-2 text-sm">
                    <span className={`w-4 shrink-0 font-display font-bold ${MEDAL[i]}`}>{i + 1}</span>
                    {entry.listing.website_url ? (
                      <a
                        href={outboundUrl(entry.listing.website_url, {
                          slug: entry.listing.slug,
                          medium: "podium",
                          content: cat.slug,
                          term: `rank-0${i + 1}`,
                        })}
                        target="_blank"
                        rel="noopener"
                        onClick={() => void trackListingClick(entry.listing.slug)}
                        className="flex min-w-0 flex-1 items-center gap-1 truncate hover:text-primary hover:underline"
                      >
                        <Globe className="size-3 shrink-0 text-muted-foreground" />
                        <span className="truncate">{entry.listing.name}</span>
                      </a>
                    ) : (
                      <Link
                        to="/p/$slug"
                        params={{ slug: entry.listing.slug }}
                        className="min-w-0 flex-1 truncate hover:underline"
                      >
                        {entry.listing.name}
                      </Link>
                    )}
                    <Button asChild variant="hot" size="sm" className="h-7 shrink-0 px-2 text-xs">
                      <Link to="/boost" search={{ listing: entry.listing.slug, category: cat.slug }}>
                        {t("boards.entry.boost")} <ArrowUpRight className="size-3" />
                      </Link>
                    </Button>
                  </li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
    </section>
  );
}
